import { X, Keyboard } from 'lucide-react'
import { formatShortcut, GLOBAL_SHORTCUTS } from '../hooks/useKeyboardShortcuts'

interface KeyboardShortcutsModalProps {
  isOpen: boolean
  onClose: () => void
}

export default function KeyboardShortcutsModal({ isOpen, onClose }: KeyboardShortcutsModalProps) {
  if (!isOpen) return null

  const shortcuts = [
    {
      category: '一般',
      items: [
        { ...GLOBAL_SHORTCUTS.NEW_TASK, label: '新しいタスクを作成' },
        { ...GLOBAL_SHORTCUTS.SEARCH, label: '検索' },
        { ...GLOBAL_SHORTCUTS.HELP, label: 'このヘルプを表示' },
        { ...GLOBAL_SHORTCUTS.CLOSE, label: 'モーダルを閉じる' },
      ]
    },
    {
      category: 'ナビゲーション',
      items: [
        { ...GLOBAL_SHORTCUTS.FOCUS_PROJECTS, label: 'プロジェクト一覧にフォーカス' },
        { ...GLOBAL_SHORTCUTS.FOCUS_TASKS, label: 'タスク一覧にフォーカス' },
        { ...GLOBAL_SHORTCUTS.TOGGLE_SIDEBAR, label: 'サイドバーの表示/非表示' },
      ]
    },
    {
      category: 'アクション',
      items: [
        { ...GLOBAL_SHORTCUTS.SAVE, label: '保存' },
        { ...GLOBAL_SHORTCUTS.TOGGLE_THEME, label: 'ダークモード切り替え' },
      ]
    },
    {
      category: 'タスク操作',
      items: [
        { key: 'Enter', label: 'タスクを編集' },
        { key: 'Delete', label: 'タスクを削除' },
        { key: 'Space', label: 'タスクの完了/未完了を切り替え' },
        { key: 'ArrowUp', label: '前のタスクに移動' },
        { key: 'ArrowDown', label: '次のタスクに移動' },
      ]
    }
  ]

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-slide-in">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
        {/* ヘッダー */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Keyboard className="h-5 w-5 text-indigo-600" />
            <h2 className="text-xl font-bold text-gray-900">キーボードショートカット</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors tap-highlight"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* コンテンツ */}
        <div className="p-6 space-y-6">
          {shortcuts.map((section) => (
            <div key={section.category}>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                {section.category}
              </h3>
              <div className="space-y-2">
                {section.items.map((item: any, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-2 px-3 rounded hover:bg-gray-50"
                  >
                    <span className="text-sm text-gray-700">
                      {item.label || item.description || ''}
                    </span>
                    <kbd className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-300 rounded">
                      {formatShortcut(item)}
                    </kbd>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* ヒント */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <p className="text-sm text-blue-800">
              <strong>ヒント:</strong> ショートカットは入力フィールド以外の場所で使用できます。
              <kbd className="mx-1 px-2 py-0.5 text-xs bg-white border border-blue-300 rounded">?</kbd>
              キーを押すと、いつでもこのヘルプを表示できます。
            </p>
          </div>
        </div>

        {/* フッター */}
        <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors tap-highlight font-medium"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  )
}
